import React from "react";
import BaseWidget, {WidgetState} from "../../../widgets/BaseWidget";
import {WidgetType} from "../../../constants/WidgetConstants";
import {PropertyPaneConfig} from "../../../constants/PropertyControlConstants";
import DynamicFormsWidget, {dateType} from "../component";
import {ValidationTypes} from "../../../constants/WidgetValidation";
import {EventType} from "../../../constants/AppsmithActionConstants/ActionConstants";
import {LabelPosition} from "../../../components/constants";
import _ from "lodash";
import moment from "moment";
import {Stylesheet} from "../../../entities/AppTheming";
import panelConfig from "./PanelConfig";
import {DerivedPropertiesMap} from "../../../utils/WidgetFactory";
import {AudioRecorderWidgetProps} from "../../../widgets/AudioRecorderWidget/widget";
import {Alignment} from "@blueprintjs/core";
import {BaseInputWidgetProps} from "../../../widgets/BaseInputWidget/widget";

class DynamicFormsV2 extends BaseWidget<AudioRecorderWidgetProps, WidgetState> {
  dynamicFormRef: any;

  constructor(props: any) {
    super(props);
    this.dynamicFormRef = React.createRef();
    this.updateWidgetMeta = this.updateWidgetMeta.bind(this);
  }

  static getPropertyPaneEventConfig(): PropertyPaneConfig[] {
    return super.getWidgetEvents('DynamicFormsWidget');
  }

  static getPropertyPaneStyleConfig() {
    return [
      {
        sectionName: "标签样式",
        children: [
          {
            propertyName: "labelTextColor",
            label: "字体颜色",
            helpText: "Control the color of the label associated",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "labelTextSize",
            label: "字体大小",
            helpText: "Control the font size of the label associated",
            controlType: "DROP_DOWN",
            defaultValue: "0.875rem",
            options: [
              {
                label: "S",
                value: "0.875rem",
                subText: "0.875rem",
              },
              {
                label: "M",
                value: "1rem",
                subText: "1rem",
              },
              {
                label: "L",
                value: "1.25rem",
                subText: "1.25rem",
              },
              {
                label: "XL",
                value: "1.875rem",
                subText: "1.875rem",
              },
              {
                label: "XXL",
                value: "3rem",
                subText: "3rem",
              },
              {
                label: "3XL",
                value: "3.75rem",
                subText: "3.75rem",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "labelStyle",
            label: "加粗&斜体",
            helpText: "Control if the label should be bold or italics",
            controlType: "BUTTON_GROUP",
            options: [
              {
                icon: "text-bold",
                value: "BOLD",
              },
              {
                icon: "text-italic",
                value: "ITALIC",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "marginTop",
            label: "上边距",
            min: 0,
            controlType: "NUMERIC_INPUT",
            isJSConvertible: false,
            isBindProperty: true,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            propertyName: "marginBottom",
            label: "下边距",
            min: 0,
            controlType: "NUMERIC_INPUT",
            isJSConvertible: false,
            isBindProperty: true,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            propertyName: "marginLeft",
            label: "左边距",
            min: 0,
            controlType: "NUMERIC_INPUT",
            isJSConvertible: false,
            isBindProperty: true,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            propertyName: "marginRight",
            label: "右边距",
            min: 0,
            controlType: "NUMERIC_INPUT",
            isJSConvertible: false,
            isBindProperty: true,
            validation: { type: ValidationTypes.NUMBER },
          },
        ],
      },
      {
        sectionName: "输入文字",
        children: [
          {
            propertyName: "inputTextColor",
            label: "字体颜色",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^(?![<|{{]).+/,
              },
            },
          },
          {
            propertyName: "inputTextSize",
            label: "字体大小",
            helpText: "Control the font size of the label associated",
            controlType: "DROP_DOWN",
            defaultValue: "0.875rem",
            options: [
              {
                label: "S",
                value: "0.875rem",
                subText: "0.875rem",
              },
              {
                label: "M",
                value: "1rem",
                subText: "1rem",
              },
              {
                label: "L",
                value: "1.25rem",
                subText: "1.25rem",
              },
              {
                label: "XL",
                value: "1.875rem",
                subText: "1.875rem",
              },
              {
                label: "XXL",
                value: "3rem",
                subText: "3rem",
              },
              {
                label: "3XL",
                value: "3.75rem",
                subText: "3.75rem",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "inputStyle",
            label: "加粗&斜体",
            helpText: "Control if the label should be bold or italics",
            controlType: "BUTTON_GROUP",
            options: [
              {
                icon: "text-bold",
                value: "BOLD",
              },
              {
                icon: "text-italic",
                value: "ITALIC",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ],
      },
      {
        sectionName: "背景",
        children: [
          {
            propertyName: "backgroundColor",
            helpText: "Sets the checked state color of the checkbox",
            label: "背景颜色",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "borderColor",
            helpText: "Sets the checked state color of the checkbox",
            label: "边框颜色",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
        ],
      },
      {
        sectionName: "边框&阴影",
        children: [
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "borderWidth",
            label: "边框宽度",
            placeholderText: "Enter value in px",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.NUMBER},
          },
          {
            propertyName: "borderRadius",
            label: "圆角大小",
            helpText:
              "Rounds the corners of the icon button's outer border edge",
            controlType: "BORDER_RADIUS_OPTIONS",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "boxShadow",
            label: "阴影",
            helpText:
              "Enables you to cast a drop shadow from the frame of the widget",
            controlType: "BOX_SHADOW_OPTIONS",
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
        ],
      },
    ];
  }

  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "label",
            label: "组件标签",
            helpText: "设置该组件的标签",
            controlType: "INPUT_TEXT",
            placeholderText: "",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            helpText: "",
            propertyName: "labelPosition",
            label: "标签位置",
            controlType: "ICON_TABS",
            fullWidth: true,
            options: [
              {label: "居左", value: LabelPosition.Left},
              {label: "居上", value: LabelPosition.Top},
            ],
            defaultValue: LabelPosition.Top,
            isBindProperty: false,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.NUMBER},
          },
          {
            helpText: "Sets the label alignment of the widget",
            propertyName: "labelAlignment",
            label: "对齐方式",
            controlType: "LABEL_ALIGNMENT_OPTIONS",
            fullWidth: false,
            options: [
              {
                startIcon: "align-left",
                value: Alignment.LEFT,
              },
              {
                startIcon: "align-right",
                value: Alignment.RIGHT,
              },
            ],
            isBindProperty: false,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
            // hidden: (props: BaseInputWidgetProps) =>
            //   props.labelPosition !== LabelPosition.Left,
            // dependencies: ["labelPosition"],
          },
          {
            helpText:
              "Sets the label width of the widget as the number of columns",
            propertyName: "labelWidth",
            label: "宽度（以列为单位）",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
            hidden: (props: BaseInputWidgetProps) =>
              props.labelPosition !== LabelPosition.Left,
            dependencies: ["labelPosition"],
          },
          {
            helpText: "",
            propertyName: "colSpans",
            label: "布局",
            controlType: "ICON_TABS",
            fullWidth: true,
            options: [
              {label: "一列", value: 24},
              {label: "二列", value: 12},
              {label: "三列", value: 8},
              {label: "四列", value: 6},
            ],
            defaultValue: 24,
            isBindProperty: false,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.NUMBER},
          },
          {
            propertyName: "isVisible",
            helpText:
              "Controls the visibility of the widget. Can also be configured the using {{currentItem}} binding.",
            label: "是否可见",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            defaultValue: true,
            validation: {
              type: ValidationTypes.ARRAY_OF_TYPE_OR_TYPE,
              params: {
                type: ValidationTypes.BOOLEAN,
              },
            }
          },
          {
            propertyName: "isDisabled",
            helpText:
              "Disables input to the widget. Can also be configured the using {{currentItem}} binding.",
            label: "是否禁用",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            defaultValue: false,
            validation: {
              type: ValidationTypes.ARRAY_OF_TYPE_OR_TYPE,
              params: {
                type: ValidationTypes.BOOLEAN,
              },
            },
            customJSControl: "MENU_BUTTON_DYNAMIC_ITEMS",
            evaluatedDependencies: ["sourceData"],
          },
          {
            helpText: "Readonly input to this widget",
            propertyName: "isReadonly",
            label: "是否只读",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            defaultValue: false,
            validation: {type: ValidationTypes.BOOLEAN},
            customJSControl: "MENU_BUTTON_DYNAMIC_ITEMS",
            evaluatedDependencies: ["sourceData"],
          },
        ],
      },
      {
        sectionName: "按钮",
        children: [
          {
            helpText: "",
            propertyName: "buttons",
            label: "",
            controlType: "FORM_BUTTON",
            isBindProperty: true,
            isTriggerProperty: false,
            panelConfig: panelConfig as any,
          }
        ]
      },
      {
        sectionName: "数据",
        children: [
          {
            propertyName: "currentData",
            helpText: "Input JSON sample for default form layout",
            label: "Source data",
            controlType: "INPUT_TEXT",
            placeholderText: '[{ "id": "name", "name": "姓名", "type": "input" }]',
            inputType: "ARRAY",
            isBindProperty: true,
            isTriggerProperty: false,
            isJSConvertible: true,
            validation: {type: ValidationTypes.ARRAY}
          }
        ]
      }
    ];
  }
  getLabelWidth = () => {
    return (Number(this.props.labelWidth) || 0) * this.props.parentColumnSpace;
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      getValue: `{{this.value}}`
    };
  }

  static getStylesheetConfig(): Stylesheet {
    return {
      borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      boxShadow: "{{appsmith.theme.boxShadow.appBoxShadow}}",
      primaryBtnColor: "{{appsmith.theme.colors.primaryColor}}",
      buttons: {
        submit: {
          index: "0",
          id: "submit",
          label: "提交",
          buttonVariant: "primary",
          iconName: "submit",
          textColor: "#F8FAFC",
          buttonColor: "{{appsmith.theme.colors.primaryColor}}",
          disabledWhenInvalid: "true",
          resetFormOnClick: "true",
          isVisible: "true",
        },
        reset: {
          index: "1",
          id: "reset",
          label: "重置",
          buttonVariant: "secondary",
          iconName: "reset",
          buttonColor: "{{appsmith.theme.colors.primaryColor}}",
          textColor: "{{appsmith.theme.colors.primaryColor}}",
          disabledWhenInvalid: "true",
          resetFormOnClick: "true",
          isVisible: "true",
        },
      }
    };
  }

  onExecuteAction = (btnConfig: any, values: any, evtType: string) => {
    let formatValues:any = {};
    _.each(this.props.currentData, (item:any)=>{
      if(dateType.includes(item.type)){ //日期类型的值转换为时间戳
        formatValues[item.id] = values[item.id]?(moment(values[item.id]).valueOf()):values[item.id];
      }else{
        formatValues[item.id] = values[item.id];
      }

    })
    if (btnConfig.onClick && evtType == 'onClick') {
      this.setState({
        isLoading: true,
      });
      super.executeAction({
        triggerPropertyName: "onClick",
        dynamicString: btnConfig.onClick,
        event: {
          type: EventType.ON_SUBMIT,
        },
        globalContext: {
          [this.props.widgetName]: formatValues
        }
      });
    } else if (btnConfig.onMouseEnter && evtType == 'onMouseEnter') {
      this.setState({
        isLoading: true,
      });
      super.executeAction({
        triggerPropertyName: "onMouseEnter",
        dynamicString: btnConfig.onMouseEnter,
        event: {
          type: EventType.ON_MOUSE_ENTER,
        },
        globalContext: formatValues
      });
    }
  }

  updateWidgetMeta(values:any, key='value'){
    this.props.updateWidgetMetaProperty(key, values);
  }

  getPageView() {
    return (
      <DynamicFormsWidget ref={this.dynamicFormRef} {...this.props}
      onExecuteAction={this.onExecuteAction} labelWidth={this.getLabelWidth()} updateWidgetMeta={this.updateWidgetMeta} />
      )
  }

  static getWidgetType(): WidgetType {
    return 'DYNAMICFORMS_WIDGET_V2';
  }
}

export default DynamicFormsV2;
